import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import english from '../../data/homeCopy/en.json';
import { chartHref } from '../../data/chartLanguage';
import { dailyPreview } from '../../services/chartService';
import { readSavedChart } from '../../services/savedChart';
import { noteHomeClick, noteHomeEvent } from '../../services/homeAnalytics';
import { cardOfTheDayPath, rememberSpreadLanguage } from './homeLinks';

const PICKS = [
  { id: 'love', glyph: '♥', tone: 'pink', to: '/love-tarot-reading/', spread: true },
  { id: 'cardOfTheDay', glyph: '✦', tone: 'yellow' },
  { id: 'yesNo', glyph: '?', tone: 'green', to: '/yes-or-no-tarot/', spread: true },
  { id: 'celtic', glyph: '✚', tone: 'yellow', to: '/celtic-cross-tarot/', spread: true },
];

function isSnapshot() {
  return typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';
}

function viewerZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function firstSentences(text, count) {
  const parts = String(text || '').match(/[^.!?。！？]+[.!?。！？]+["”’»」]?\s*|[^.!?。！？]+$/g);
  if (!parts) return '';
  return parts.slice(0, count).join('').trim();
}

const PickCards = ({ copy, lang }) => {
  const text = copy.picks || english.picks;
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node || isSnapshot()) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      noteHomeEvent('home_picks_view', { language: lang });
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        noteHomeEvent('home_picks_view', { language: lang });
        observer.disconnect();
      }
    }, { threshold: 0.5 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [lang]);

  return (
    <section className="hl-side-card hl-picks" aria-labelledby="hl-picks-title" ref={ref}>
      <h2 id="hl-picks-title">{text.title}</h2>
      <p className="hl-side-sub">{text.text}</p>
      <ul className="hl-picks-grid">
        {PICKS.map((pick) => {
          const item = text[pick.id] || english.picks[pick.id];
          const href = pick.id === 'cardOfTheDay' ? cardOfTheDayPath(lang) : pick.to;
          return (
            <li key={pick.id}>
              <a
                className="hl-pick"
                href={href}
                onClick={() => {
                  if (pick.spread) rememberSpreadLanguage(lang);
                  noteHomeClick(pick.id, 'hero_picks', lang);
                }}
              >
                <span className="hl-pick-head">
                  <span className={`hl-glyph hl-${pick.tone}`} aria-hidden="true">{pick.glyph}</span>
                  <h3>{item.title}</h3>
                </span>
                <span className="hl-pick-text">{item.text}</span>
              </a>
            </li>
          );
        })}
      </ul>
      <a
        className="hl-picks-more"
        href={chartHref('/tarot-card-interpreter/', lang)}
        onClick={() => noteHomeClick('interpreter', 'hero_picks', lang)}
      >
        {text.interpreter}
      </a>
    </section>
  );
};

const AstrologyCard = ({ copy, lang }) => {
  const text = copy.astro || english.astro;
  const today = { ...english.today, ...copy.today };
  const [saved, setSaved] = useState(null);
  const [status, setStatus] = useState('loading');
  const [preview, setPreview] = useState(null);
  const [viewerDate, setViewerDate] = useState('');

  useLayoutEffect(() => {
    if (isSnapshot()) return;
    const chart = readSavedChart();
    if (chart) setSaved(chart);
    else delete document.documentElement.dataset.chart;
  }, []);

  useEffect(() => {
    if (!saved) return undefined;
    let live = true;
    setStatus('loading');
    dailyPreview({ birth: saved, viewer_timezone: viewerZone(), language: lang, saved: true })
      .then((result) => {
        if (!live) return;
        setViewerDate(result.viewer_date || '');
        const summary = firstSentences(result.preview?.summary, 2);
        if (result.preview?.headline && summary) {
          setPreview({ headline: result.preview.headline, summary });
          setStatus('ready');
        } else {
          setStatus('waiting');
        }
      })
      .catch(() => {
        if (live) setStatus('waiting');
      });
    return () => {
      live = false;
    };
  }, [saved, lang]);

  if (!saved) {
    return (
      <section className="hl-side-card hl-astro-card hl-astro-new" aria-labelledby="hl-astro-title">
        <span className="hl-astro-label">{text.newLabel}</span>
        <h2 id="hl-astro-title">{text.newTitle}</h2>
        <p className="hl-side-sub">{text.newText}</p>
        <a
          className="hl-astro-go"
          href={chartHref('/birth-chart/', lang)}
          onClick={() => noteHomeClick('birthChart', 'hero_astro', lang)}
        >
          {text.newCta}
        </a>
        <p className="hl-astro-note">{text.newNote}</p>
      </section>
    );
  }

  const dateLabel = viewerDate
    ? new Intl.DateTimeFormat(lang || 'en', { weekday: 'long', day: 'numeric', month: 'long' })
      .format(new Date(`${viewerDate}T12:00:00`))
    : '';
  const ready = status === 'ready';
  return (
    <section className="hl-side-card hl-astro-card" aria-labelledby="hl-astro-title">
      <div className="hl-astro-top">
        <h2 id="hl-astro-title" className="hl-astro-label">{today.astroLabel}</h2>
        {dateLabel && <span className="hl-astro-date">{dateLabel}</span>}
      </div>
      {status === 'loading' ? (
        <div className="hl-astro-loading" aria-live="polite">
          <span className="hl-astro-skeleton hl-astro-skeleton-title" />
          <span className="hl-astro-skeleton" />
          <span className="hl-astro-skeleton hl-astro-skeleton-short" />
          <span className="hl-sr">{today.astroLoading}</span>
        </div>
      ) : (
        <>
          <p className="hl-astro-headline">{ready ? preview.headline : text.waitTitle}</p>
          <p className="hl-side-sub">{ready ? preview.summary : text.waitText}</p>
        </>
      )}
      <a
        className="hl-astro-go"
        href={chartHref('/personalized-daily-horoscope/', lang)}
        onClick={() => noteHomeClick('dailyHoroscope', ready ? 'hero_astro_ready' : 'hero_astro', lang)}
      >
        {ready ? today.astroRead : text.waitCta}
      </a>
      <p className="hl-astro-note">{today.astroBased}</p>
    </section>
  );
};

const HomeHeroCards = ({ copy, lang }) => (
  <div className="hl-side">
    <PickCards copy={copy} lang={lang} />
    <AstrologyCard copy={copy} lang={lang} />
  </div>
);

export default HomeHeroCards;
